import React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  maxVisiblePages = 5,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const getVisiblePages = (isMobile: boolean = false) => {
    const pages: number[] = [];
    const maxPages = isMobile ? 3 : maxVisiblePages; // Show only 3 pages on mobile

    if (totalPages <= maxPages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate start and end pages
      let start = Math.max(1, currentPage - Math.floor(maxPages / 2));
      let end = Math.min(totalPages, start + maxPages - 1);

      // Adjust start if we're near the end
      if (end - start + 1 < maxPages) {
        start = Math.max(1, end - maxPages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();
  const mobileVisiblePages = getVisiblePages(true);

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-[6px] mt-8">
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="group flex items-center justify-center w-10 h-10 sm:w-15 sm:h-15 bg-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white hover:bg-[#1a5852] transition-all duration-200 cursor-pointer"
      >
        <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 text-[#3d434c] group-hover:text-white group-disabled:group-hover:text-[#3d434c] transition-colors duration-200" />
      </button>

      {/* Page Numbers - Desktop */}
      <div className="hidden sm:flex items-center gap-[6px]">
        {visiblePages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`flex items-center justify-center w-15 h-15 rounded transition-all duration-200 cursor-pointer ${
              currentPage === page
                ? "text-white bg-[#1a5852]"
                : "text-[#3d434c] bg-white hover:bg-[#1a5852] hover:text-white"
            }`}
          >
            <span
              className={`text-base font-medium leading-[16px] ${currentPage === page ? "text-white" : ""}`}
            >
              {page}
            </span>
          </button>
        ))}
      </div>

      {/* Page Numbers - Mobile */}
      <div className="flex sm:hidden items-center gap-2">
        {mobileVisiblePages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`flex items-center justify-center w-10 h-10 rounded transition-all duration-200 cursor-pointer ${
              currentPage === page
                ? "text-white bg-[#1a5852]"
                : "text-[#3d434c] bg-white hover:bg-[#1a5852] hover:text-white"
            }`}
          >
            <span
              className={`text-sm font-medium leading-[14px] ${currentPage === page ? "text-white" : ""}`}
            >
              {page}
            </span>
          </button>
        ))}
      </div>

      {/* Next Button */}
      <button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="group flex items-center justify-center w-10 h-10 sm:w-15 sm:h-15 bg-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white hover:bg-[#1a5852] transition-all duration-200 cursor-pointer"
      >
        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-[#3d434c] group-hover:text-white group-disabled:group-hover:text-[#3d434c] transition-colors duration-200" />
      </button>
    </div>
  );
}
